import React, { useId, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import CreatableSelect from 'react-select/creatable';
import places from '../../data/southAfricanPlaces.json';

type PlaceOption = { value: string; label: string; name: string; province?: string; search?: string };
const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const options: PlaceOption[] = places.map(place => ({
  value: `${place.name}|${place.province}`,
  label: `${place.name} — ${place.province}`,
  name: place.name,
  province: place.province,
  search: normalize([place.name, place.province, ...place.aliases].join(' '))
}));
const resultLimit = 100;

type Props = {
  id: string;
  value?: string;
  onChange: (city: string, province?: string) => void;
  onBlur: () => void;
  invalid?: boolean;
};

export default function SouthAfricanCitySelect({ id, value, onChange, onBlur, invalid }: Props) {
  const [input, setInput] = useState('');
  const helpId = useId();
  const terms = normalize(input.trim()).split(/\s+/).filter(Boolean);
  const matches = terms.length ? options.filter(option => terms.every(term => option.search!.includes(term))) : [];
  // Saved strings (including historic names) are display values, never lookup-driven edits.
  const current: PlaceOption | null = value ? { value, label: value, name: value } : null;
  return <>
    <div className="d-flex align-items-center gap-2">
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
    <CreatableSelect<PlaceOption, false>
      inputId={id} instanceId={id} value={current}
      options={matches.slice(0, resultLimit)} filterOption={null}
      inputValue={input} onInputChange={setInput}
      onChange={option => onChange(option?.name || '', option?.province)}
      onCreateOption={name => onChange(name.trim())}
      // Even a known name can be captured manually without deriving a province.
      isValidNewOption={name => !!name.trim()}
      formatCreateLabel={name => `Use “${name.trim()}” (enter manually)`}
      onBlur={onBlur} isSearchable isClearable
      aria-invalid={invalid} aria-describedby={`${id}-help`}
      placeholder="Search for a city, town or village…"
      noOptionsMessage={() => 'Type a place name to search or enter it manually'}
      styles={{ control: (base, state) => ({ ...base, minHeight: 38,
        borderColor: invalid ? '#dc3545' : state.isFocused ? '#198754' : base.borderColor }) }}
    />
      </div>
      <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={<Popover id={`city-help-${helpId}`}>
        <Popover.Header as="h3">City selection</Popover.Header>
        <Popover.Body>
      Choose a listed place to set its province, or enter a place manually. Clear the city to change the province.
      {' '}Place list: <a href="https://www.geonames.org/" target="_blank" rel="noreferrer">GeoNames</a>
      {' '}(<a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noreferrer">CC BY 4.0</a>),
      {' '}21 October 2026; coverage is not exhaustive.
        </Popover.Body>
      </Popover>}><button type="button" className="btn btn-link p-0" aria-label="About city selection">ⓘ</button></OverlayTrigger>
    </div>
    <span id={`${id}-help`} className="visually-hidden">Search for a place or enter it manually. Open city selection help for guidance.</span>
    {matches.length > resultLimit && <small className="text-muted" role="status">Showing the first 100 matches. Refine your search.</small>}
  </>;
}
